/**
 * Client-side session sync revisions — tracks server revision counters and
 * requests `session:sync` when stale.
 */

const revisionStore = {
  exercise_plan: 0,
  table_setup: 0,
  hr_by_te: {},
  exercise_timer_by_te: {},
  kot_by_te: {},
};

export function getClientRevisions() {
  return {
    exercise_plan: revisionStore.exercise_plan,
    table_setup: revisionStore.table_setup,
    hr_by_te: { ...revisionStore.hr_by_te },
    exercise_timer_by_te: { ...revisionStore.exercise_timer_by_te },
    kot_by_te: { ...revisionStore.kot_by_te },
  };
}

export function applyServerRevisions(serverRevisions) {
  if (!serverRevisions || typeof serverRevisions !== "object") return;
  if (serverRevisions.exercise_plan != null) {
    revisionStore.exercise_plan = Number(serverRevisions.exercise_plan) || 0;
  }
  if (serverRevisions.table_setup != null) {
    revisionStore.table_setup = Number(serverRevisions.table_setup) || 0;
  }
  mergeRevMap(revisionStore.hr_by_te, serverRevisions.hr_by_te);
  mergeRevMap(revisionStore.exercise_timer_by_te, serverRevisions.exercise_timer_by_te);
  mergeRevMap(revisionStore.kot_by_te, serverRevisions.kot_by_te);
}

function mergeRevMap(target, incoming) {
  if (!incoming || typeof incoming !== "object") return;
  for (const [k, v] of Object.entries(incoming)) {
    target[k] = Number(v) || 0;
  }
}

export function noteExerciseTimerRevision(teId, revision) {
  if (!teId || revision == null) return;
  const r = Number(revision);
  if (!Number.isFinite(r)) return;
  const prev = revisionStore.exercise_timer_by_te[String(teId)] || 0;
  if (r > prev) revisionStore.exercise_timer_by_te[String(teId)] = r;
}

export function noteKotSnapshotRevision(teId, revision) {
  if (!teId || revision == null) return;
  const r = Number(revision);
  if (!Number.isFinite(r)) return;
  const prev = revisionStore.kot_by_te[String(teId)] || 0;
  if (r > prev) revisionStore.kot_by_te[String(teId)] = r;
}

export function isStaleExerciseTimerEvent(teId, revision) {
  if (revision == null || teId == null) return false;
  const r = Number(revision);
  if (!Number.isFinite(r)) return false;
  const prev = revisionStore.exercise_timer_by_te[String(teId)] || 0;
  return r <= prev;
}

/**
 * Emit session:sync and apply returned domains via callbacks.
 * @param {import("socket.io-client").Socket} socket
 * @param {object} params
 * @param {string} params.tsId
 * @param {string} [params.activeTeId]
 * @param {(domains: object) => void} [params.onDomains]
 */
export function requestSessionSync(socket, { tsId, activeTeId, onDomains }) {
  if (!socket || !tsId) return Promise.resolve(null);
  return new Promise((resolve) => {
    socket.emit(
      "session:sync",
      {
        ts_id: tsId,
        active_te_id: activeTeId || undefined,
        client_revisions: getClientRevisions(),
      },
      (ack) => {
        if (ack?.server_revisions) applyServerRevisions(ack.server_revisions);
        if (ack?.domains && onDomains) onDomains(ack.domains);
        resolve(ack);
      }
    );
  });
}

/**
 * Patch exercise rows from session:sync exercise_plan_delta + timer snapshots.
 */
export function patchExercisesFromSyncDomains(prevList, domains) {
  if (!Array.isArray(prevList) || !domains) return prevList;
  let next = prevList;

  if (Array.isArray(domains.exercise_plan_delta) && domains.exercise_plan_delta.length) {
    const byId = new Map(prevList.map((ex) => [String(ex.te_id), ex]));
    for (const row of domains.exercise_plan_delta) {
      if (!row?.te_id) continue;
      const key = String(row.te_id);
      byId.set(key, { ...(byId.get(key) || {}), ...row });
    }
    next = Array.from(byId.values()).sort(
      (a, b) => (a.te_order ?? 0) - (b.te_order ?? 0)
    );
  }

  const timerSnaps = domains.exercise_timer_snapshots;
  if (timerSnaps && typeof timerSnaps === "object") {
    next = next.map((ex) => {
      const snap = timerSnaps[String(ex.te_id)];
      if (!snap) return ex;
      noteExerciseTimerRevision(ex.te_id, snap.exercise_timer_revision);
      return mergeExerciseTimerSnap(ex, snap);
    });
  }

  return next;
}

export function mergeExerciseTimerSnap(ex, snap) {
  if (!snap) return ex;
  const exerciseRemaining = snap.timer ?? snap.remaining_exercise_time_seconds;
  const sequenceRemaining = snap.sequenceTimer ?? snap.remaining_sequence_time_seconds;
  return {
    ...ex,
    te_state: snap.te_state ?? ex.te_state,
    tbl_state_te: snap.tbl_state_te ?? ex.tbl_state_te,
    timer: exerciseRemaining ?? ex.timer,
    sequenceTimer: sequenceRemaining ?? ex.sequenceTimer,
    currentSequence:
      snap.currentSequence !== undefined ? snap.currentSequence : ex.currentSequence,
    synchronizedStartTime: snap.synchronizedStartTime ?? ex.synchronizedStartTime,
    sequenceStartTime: snap.sequenceStartTime ?? ex.sequenceStartTime,
    media_sync_at: snap.media_sync_at ?? snap.mediaSyncAt ?? ex.media_sync_at,
    exercise_timer_revision: snap.exercise_timer_revision ?? ex.exercise_timer_revision,
  };
}
